import { Link } from "react-router-dom";
import { Phone, MapPin, GraduationCap, Sparkles, ArrowRight, Calendar, BookOpen, MessageCircle, Heart, Stethoscope, Quote } from "lucide-react";
import { useLanguage } from "@/i18n/LanguageContext";
import PageWrapper from "@/components/layout/PageWrapper";
import StarRating from "@/components/StarRating";
import ReviewsSlider from "@/components/ReviewsSlider";
import Icon from "@/components/Icon";
import { CONTACT, IMAGES, PILLARS, SCHOOL_SERVICES, FAQS, SCHOOL_REVIEWS } from "@/data/content";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

const Home = () => {
  const { t } = useLanguage();

  return (
    <PageWrapper
      title="ADHD School Bhadrak | Aachary Dev Development Holistic School"
      description="Aachary Dev Development Holistic School (ADHD School) — special education, occupational, physiotherapy, speech and sensory integration therapy for children in Bhadrak, Odisha. 4.9 rating, 150+ reviews."
    >
      {/* HERO */}
      <section
        data-testid="home-hero"
        className="relative overflow-hidden"
        style={{
          background:
            "radial-gradient(60% 80% at 100% 0%, rgba(129,195,215,0.22) 0%, transparent 60%), radial-gradient(50% 70% at 0% 100%, rgba(90,147,103,0.18) 0%, transparent 60%), var(--adhd-bg)",
        }}
      >
        <div className="adhd-container relative grid lg:grid-cols-12 gap-10 lg:gap-14 py-16 lg:py-24">
          <div className="lg:col-span-7 flex flex-col gap-7">
            <p className="adhd-eyebrow">
              <MapPin size={13} />
              {t("hero.eyebrow")}
            </p>
            <h1 className="font-serif text-[40px] leading-[1.05] sm:text-5xl lg:text-[68px] tracking-tight text-[color:var(--adhd-text)]">
              <span className="block">{t("brand.school")}</span>
              <span
                className="mt-3 block text-[26px] sm:text-3xl lg:text-4xl font-medium"
                style={{ color: "var(--adhd-primary)" }}
              >
                {t("hero.headline")}
              </span>
            </h1>
            <p className="text-base sm:text-lg leading-relaxed text-[color:var(--adhd-muted)] max-w-2xl">
              {t("hero.subheading")}
            </p>

            <div className="flex flex-wrap gap-3">
              <Link to="/contact" data-testid="hero-enquiry-btn" className="adhd-btn-primary">
                {t("cta.enquire")} <ArrowRight size={14} />
              </Link>
              <a
                href={`tel:${CONTACT.phoneTel}`}
                data-testid="hero-call-btn"
                className="adhd-btn-secondary"
              >
                <Phone size={14} /> {t("cta.call")}
              </a>
              <Link
                to="/principal"
                data-testid="hero-principal-btn"
                className="adhd-btn-accent"
              >
                <GraduationCap size={14} /> {t("cta.principal")}
              </Link>
              <a
                href={CONTACT.mapShareUrl}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="hero-location-btn"
                className="inline-flex items-center gap-2 px-5 py-3.5 text-sm font-semibold rounded-full text-[color:var(--adhd-text)] hover:bg-white transition-colors"
              >
                <MapPin size={14} /> {t("cta.location")}
              </a>
            </div>

            {/* Trust ribbon */}
            <div className="mt-6 flex flex-wrap items-center gap-6">
              <div className="flex items-center gap-3">
                <StarRating rating={CONTACT.rating} size={16} testId="hero-rating" />
                <div>
                  <p className="font-semibold text-[color:var(--adhd-text)]">
                    {CONTACT.rating} {t("hero.rating")}
                  </p>
                  <p className="text-xs text-[color:var(--adhd-muted)]">
                    {CONTACT.reviewCount}+ {t("hero.reviews")} · Google
                  </p>
                </div>
              </div>
              <div className="h-10 w-px bg-[color:var(--adhd-border)] hidden sm:block" />
              <div className="flex items-center gap-2">
                <span className="grid place-items-center h-9 w-9 rounded-full" style={{ background: "rgba(58,124,165,0.1)" }}>
                  <Heart size={15} style={{ color: "var(--adhd-primary)" }} />
                </span>
                <div>
                  <p className="text-sm font-semibold text-[color:var(--adhd-text)]">
                    {t("hero.statTherapies")}
                  </p>
                  <p className="text-xs text-[color:var(--adhd-muted)]">
                    {t("hero.statYears")}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Hero collage */}
          <div className="lg:col-span-5 relative">
            <div className="relative aspect-[4/5] sm:aspect-[4/4.4] rounded-[2rem] overflow-hidden shadow-[0_30px_80px_-30px_rgba(43,58,66,0.35)]">
              <img
                src={IMAGES.heroSchool}
                alt="Children at ADHD School Bhadrak"
                decoding="async"
                fetchpriority="high"
                width="900"
                height="1100"
                className="h-full w-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#0f1e26]/40 via-transparent to-transparent" />
              <div className="absolute bottom-5 left-5 right-5 flex items-end justify-between">
                <div className="bg-white/95 backdrop-blur rounded-2xl px-4 py-3 shadow-lg">
                  <p className="text-[11px] uppercase tracking-wider text-[color:var(--adhd-muted)]">
                    Established
                  </p>
                  <p className="font-serif text-lg text-[color:var(--adhd-text)]">
                    Bhadrak, Odisha
                  </p>
                </div>
                <span className="adhd-bob grid place-items-center h-14 w-14 rounded-full bg-[color:var(--adhd-accent)] text-[color:var(--adhd-text)] shadow-lg">
                  <Sparkles size={22} />
                </span>
              </div>
            </div>

            {/* Floating stat card */}
            <div className="hidden md:flex absolute -left-6 bottom-12 adhd-card p-4 items-center gap-3 max-w-[220px]">
              <span className="grid place-items-center h-11 w-11 rounded-full" style={{ background: "rgba(90,147,103,0.16)" }}>
                <BookOpen size={18} style={{ color: "var(--adhd-success)" }} />
              </span>
              <div>
                <p className="text-xs uppercase tracking-wider text-[color:var(--adhd-muted)]">
                  Curriculum
                </p>
                <p className="font-semibold text-sm text-[color:var(--adhd-text)]">
                  Holistic & Individualised
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ABOUT — WHY TRUST */}
      <section data-testid="home-about" className="adhd-section" id="about">
        <div className="adhd-container grid lg:grid-cols-12 gap-12 items-start">
          <div className="lg:col-span-5">
            <p className="adhd-eyebrow">
              <Heart size={13} /> About ADHD School
            </p>
            <h2 className="mt-3 font-serif text-3xl sm:text-4xl lg:text-5xl leading-tight text-[color:var(--adhd-text)]">
              {t("about.title")}
            </h2>
            <p className="mt-5 text-base leading-relaxed text-[color:var(--adhd-muted)]">
              {t("about.body")}
            </p>
            <div className="mt-7 flex flex-wrap gap-3">
              <Link to="/about" data-testid="about-readmore" className="adhd-btn-secondary">
                {t("cta.readMore")} <ArrowRight size={14} />
              </Link>
              <a
                href={`tel:${CONTACT.phoneTel}`}
                className="text-sm font-semibold text-[color:var(--adhd-primary)] hover:underline inline-flex items-center gap-1.5"
              >
                <Phone size={13} /> {CONTACT.phone}
              </a>
            </div>
          </div>
          <div className="lg:col-span-7">
            <p className="text-sm uppercase tracking-[0.18em] font-semibold text-[color:var(--adhd-muted)] mb-5">
              {t("about.pillarsTitle")}
            </p>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              {PILLARS.map((p) => (
                <div
                  key={p.key}
                  data-testid={`pillar-${p.key}`}
                  className="adhd-card p-4 flex flex-col gap-3"
                >
                  <span
                    className="grid place-items-center h-10 w-10 rounded-xl"
                    style={{ background: "rgba(58,124,165,0.10)" }}
                  >
                    <Icon name={p.icon} size={18} className="text-[color:var(--adhd-primary)]" />
                  </span>
                  <p className="text-sm font-semibold leading-snug text-[color:var(--adhd-text)]">
                    {p.label}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* SERVICES PREVIEW */}
      <section
        data-testid="home-services"
        className="adhd-section"
        id="services"
        style={{ background: "var(--adhd-bg-soft)" }}
      >
        <div className="adhd-container">
          <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-5">
            <div className="max-w-2xl">
              <p className="adhd-eyebrow">
                <BookOpen size={13} /> Therapy & Education
              </p>
              <h2 className="mt-3 font-serif text-3xl sm:text-4xl lg:text-5xl text-[color:var(--adhd-text)]">
                {t("services.title")}
              </h2>
              <p className="mt-3 text-base text-[color:var(--adhd-muted)]">
                {t("services.subtitle")}
              </p>
            </div>
            <Link to="/services" data-testid="services-viewall" className="adhd-btn-secondary">
              {t("cta.viewAll")} <ArrowRight size={14} />
            </Link>
          </div>

          {/* Bento grid */}
          <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-6 gap-5 auto-rows-fr">
            {SCHOOL_SERVICES.map((s, i) => {
              const span =
                i === 0
                  ? "lg:col-span-3 lg:row-span-2"
                  : i === 1 || i === 2
                  ? "lg:col-span-3"
                  : "lg:col-span-2";
              return (
                <article
                  key={s.id}
                  data-testid={`service-card-${s.id}`}
                  className={`adhd-card p-6 sm:p-7 flex flex-col gap-4 ${span}`}
                >
                  <span
                    className="grid place-items-center h-12 w-12 rounded-2xl"
                    style={{
                      background:
                        i === 0
                          ? "rgba(58,124,165,0.12)"
                          : "rgba(129,195,215,0.18)",
                      color: "var(--adhd-primary)",
                    }}
                  >
                    <Icon name={s.icon} size={22} />
                  </span>
                  <div>
                    <h3 className="font-serif text-xl sm:text-2xl text-[color:var(--adhd-text)] leading-snug">
                      {s.title}
                    </h3>
                    <p className="mt-2 text-sm leading-relaxed text-[color:var(--adhd-muted)]">
                      {s.desc}
                    </p>
                  </div>
                </article>
              );
            })}
          </div>
        </div>
      </section>

      {/* PRINCIPAL PREVIEW */}
      <section data-testid="home-principal" className="adhd-section" id="principal">
        <div className="adhd-container grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-5">
            <div className="relative">
              <div className="aspect-[4/5] rounded-[2rem] overflow-hidden border border-[color:var(--adhd-border)]">
                <img
                  src={IMAGES.principal}
                  alt={t("principal.name")}
                  loading="lazy"
                  decoding="async"
                  className="h-full w-full object-cover"
                />
              </div>
              <div className="hidden sm:block absolute -bottom-6 -right-6 adhd-card p-5 max-w-[260px]">
                <Quote size={20} className="opacity-30" style={{ color: "var(--adhd-primary)" }} />
                <p className="mt-2 text-sm leading-relaxed italic text-[color:var(--adhd-text)]">
                  "Every child is a universe waiting to be understood."
                </p>
              </div>
            </div>
          </div>
          <div className="lg:col-span-7">
            <p className="adhd-eyebrow">
              <GraduationCap size={13} /> {t("principal.title")}
            </p>
            <h2 className="mt-3 font-serif text-3xl sm:text-4xl lg:text-5xl text-[color:var(--adhd-text)]">
              {t("principal.name")}
            </h2>
            <p className="mt-1 text-[color:var(--adhd-muted)]">{t("principal.role")}</p>
            <div className="mt-5 flex flex-col gap-2 text-sm text-[color:var(--adhd-text)]/85">
              <p><span className="font-semibold">Qualifications: </span>{t("principal.qualifications")}</p>
              <p><span className="font-semibold">Certifications: </span>{t("principal.certs")}</p>
            </div>
            <blockquote className="mt-6 border-l-4 pl-5 py-1 italic text-lg text-[color:var(--adhd-text)]" style={{ borderColor: "var(--adhd-accent)" }}>
              "{t("principal.quote")}"
            </blockquote>
            <Link to="/principal" data-testid="principal-cta" className="mt-7 adhd-btn-primary inline-flex">
              {t("cta.principal")} <ArrowRight size={14} />
            </Link>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section
        data-testid="home-faq"
        className="adhd-section"
        id="faq"
        style={{ background: "var(--adhd-bg-soft)" }}
      >
        <div className="adhd-container grid lg:grid-cols-12 gap-10">
          <div className="lg:col-span-4">
            <p className="adhd-eyebrow">
              <MessageCircle size={13} /> Parents ask
            </p>
            <h2 className="mt-3 font-serif text-3xl sm:text-4xl lg:text-5xl text-[color:var(--adhd-text)]">
              {t("faq.title")}
            </h2>
            <p className="mt-3 text-base text-[color:var(--adhd-muted)]">{t("faq.subtitle")}</p>
            <a
              href={`tel:${CONTACT.phoneTel}`}
              className="mt-6 adhd-btn-primary inline-flex"
            >
              <Phone size={14} /> {t("cta.call")}
            </a>
          </div>
          <div className="lg:col-span-8">
            <Accordion type="single" collapsible className="flex flex-col gap-3" data-testid="faq-accordion">
              {FAQS.map((f, i) => (
                <AccordionItem
                  key={i}
                  value={`item-${i}`}
                  data-testid={`faq-item-${i}`}
                  className="adhd-card !rounded-2xl px-5 sm:px-6"
                >
                  <AccordionTrigger className="text-left text-base sm:text-lg font-semibold hover:no-underline">
                    {f.q}
                  </AccordionTrigger>
                  <AccordionContent className="text-[color:var(--adhd-muted)] leading-relaxed text-sm sm:text-base">
                    {f.a}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </div>
      </section>

      {/* REVIEWS */}
      <section data-testid="home-reviews" className="adhd-section" id="reviews">
        <div className="adhd-container">
          <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-5">
            <div>
              <p className="adhd-eyebrow">⭐ Google Reviews</p>
              <h2 className="mt-3 font-serif text-3xl sm:text-4xl lg:text-5xl text-[color:var(--adhd-text)]">
                {t("reviews.title")}
              </h2>
              <p className="mt-3 text-base text-[color:var(--adhd-muted)]">{t("reviews.subtitle")}</p>
            </div>
            <div className="flex items-center gap-3">
              <div className="text-center">
                <p className="font-serif text-4xl text-[color:var(--adhd-primary)] leading-none">
                  {CONTACT.rating}
                </p>
                <StarRating rating={CONTACT.rating} size={14} className="mt-1.5" />
                <p className="text-xs text-[color:var(--adhd-muted)] mt-1">
                  {CONTACT.reviewCount}+ reviews
                </p>
              </div>
            </div>
          </div>
        </div>
        <div className="mt-10">
          <ReviewsSlider reviews={SCHOOL_REVIEWS} />
        </div>
      </section>

      {/* PHYSIO TEASER */}
      <section
        data-testid="home-physio-teaser"
        className="adhd-section"
      >
        <div className="adhd-container">
          <div
            className="relative overflow-hidden rounded-[2.5rem] grid lg:grid-cols-12 gap-0"
            style={{ background: "#0f1e26", color: "#e8eef0" }}
          >
            <div className="lg:col-span-7 p-9 sm:p-12 lg:p-16 flex flex-col gap-5 justify-center">
              <p className="text-[11px] uppercase tracking-[0.22em] font-semibold" style={{ color: "var(--adhd-accent)" }}>
                {t("physio.eyebrow")}
              </p>
              <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl leading-tight text-white">
                {t("physio.title")}
              </h2>
              <p className="text-[15px] leading-relaxed opacity-85 max-w-xl">
                {t("physio.tagline")} — guided by{" "}
                <span className="text-white font-semibold">Dr. Manas Ranjan Jena</span>, Consultant & Parent Counselor.
              </p>
              <div className="flex flex-wrap gap-3 mt-3">
                <Link
                  to="/physiotherapy"
                  data-testid="physio-explore-btn"
                  className="adhd-btn-accent"
                >
                  Explore Rehab Centre <ArrowRight size={14} />
                </Link>
                <Link
                  to="/doctor"
                  data-testid="physio-doctor-btn"
                  className="inline-flex items-center gap-2 px-5 py-3.5 text-sm font-semibold rounded-full border border-white/20 text-white hover:bg-white/10 transition-colors"
                >
                  <Stethoscope size={14} /> {t("cta.doctor")}
                </Link>
              </div>
            </div>
            <div className="lg:col-span-5 relative min-h-[260px]">
              <img
                src="/home-physio.jpeg"
                alt="Bhadrak Physiotherapy & Rehab Centre"
                loading="lazy"
                decoding="async"
                className="absolute inset-0 h-full w-full object-cover opacity-90"
              />
              <div className="absolute inset-0" style={{ background: "linear-gradient(90deg, rgba(15,30,38,1) 0%, rgba(15,30,38,0) 35%)" }} />
            </div>
          </div>
        </div>
      </section>

      {/* CTA STRIP — events / brochure */}
      <section data-testid="home-cta-strip" className="adhd-container pb-10">
        <div className="grid sm:grid-cols-2 gap-5">
          <div className="adhd-card p-7 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              <span className="grid place-items-center h-12 w-12 rounded-2xl" style={{ background: "rgba(230,194,41,0.18)" }}>
                <Calendar size={20} style={{ color: "#b58c00" }} />
              </span>
              <div>
                <p className="font-serif text-xl text-[color:var(--adhd-text)]">Events & Workshops</p>
                <p className="text-sm text-[color:var(--adhd-muted)]">
                  See what's happening this season at ADHD School.
                </p>
              </div>
            </div>
            <Link to="/events" data-testid="cta-events" className="adhd-btn-secondary !py-2.5">
              View <ArrowRight size={14} />
            </Link>
          </div>
          <div className="adhd-card p-7 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              <span className="grid place-items-center h-12 w-12 rounded-2xl" style={{ background: "rgba(90,147,103,0.18)" }}>
                <BookOpen size={20} style={{ color: "var(--adhd-success)" }} />
              </span>
              <div>
                <p className="font-serif text-xl text-[color:var(--adhd-text)]">Download Prospectus</p>
                <p className="text-sm text-[color:var(--adhd-muted)]">
                  Curriculum, fees and admission process — at a glance.
                </p>
              </div>
            </div>
            <Link
              to="/contact"
              data-testid="cta-brochure"
              className="adhd-btn-accent !py-2.5"
            >
              {t("cta.brochure")}
            </Link>
          </div>
        </div>
      </section>

      {/* JSON-LD: FAQ schema */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: FAQS.map((f) => ({
              "@type": "Question",
              name: f.q,
              acceptedAnswer: { "@type": "Answer", text: f.a },
            })),
          }),
        }}
      />
    </PageWrapper>
  );
};

export default Home;
